import React, { useState, useMemo } from 'react';
import { X, ExternalLink, Sparkles, CheckCircle2, AlertCircle, Users, Building2, Loader2 } from 'lucide-react';
import { useSearchRun } from '../../hooks/useSearchRun';
import { useAuth } from '../../hooks/useAuth';

const PRIORITY_BADGE = {
  high: 'bg-emerald-100 text-emerald-800',
  medium: 'bg-amber-100 text-amber-800',
  low: 'bg-red-100 text-red-700',
};

export default function SearchResultsPanel({ results, runId, onClose }) {
  const { searchTarget, apolloResults, classifications, summary, totalCount, empresaContextId } = results;
  const { user } = useAuth();
  const { enrichPeople } = useSearchRun();

  const classMap = useMemo(() => {
    const m = new Map();
    (classifications || []).forEach((c) => {
      const key = searchTarget === 'people' ? c.apollo_person_id : c.apollo_id;
      if (key) m.set(key, c);
    });
    return m;
  }, [classifications, searchTarget]);

  const [filter, setFilter] = useState('all');
  const [selected, setSelected] = useState(new Set());
  const [enriching, setEnriching] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const rows = useMemo(() => {
    return (apolloResults || []).map((r) => {
      const key = searchTarget === 'people' ? r.apollo_person_id : r.apollo_id;
      const cls = classMap.get(key);
      return { ...r, priority: cls?.priority || 'low', reasoning: cls?.reasoning, suggested_tipo_empresa: cls?.suggested_tipo_empresa };
    });
  }, [apolloResults, classMap, searchTarget]);

  const filtered = useMemo(() => {
    if (filter === 'all') return rows;
    if (filter === 'in_crm') return rows.filter((r) => r.already_in_crm);
    return rows.filter((r) => r.priority === filter);
  }, [rows, filter]);

  const counts = useMemo(() => {
    const out = { high: 0, medium: 0, low: 0, in_crm: 0 };
    rows.forEach((r) => {
      if (r.already_in_crm) out.in_crm++;
      if (r.priority in out) out[r.priority]++;
    });
    return out;
  }, [rows]);

  const toggleAll = () => {
    if (selected.size === filtered.length) setSelected(new Set());
    else setSelected(new Set(filtered.map((r) => searchTarget === 'people' ? r.apollo_person_id : r.apollo_id)));
  };

  const toggleOne = (key) => {
    const next = new Set(selected);
    if (next.has(key)) next.delete(key); else next.add(key);
    setSelected(next);
  };

  const doEnrich = async (createInCrm) => {
    if (searchTarget !== 'people') { alert('Enriquecimento bulk só disponível para People'); return; }
    if (!empresaContextId && createInCrm) { alert('Sem empresa-alvo: não é possível criar contatos (contact_employments compliance). Use o "Enriquecer" individual em preview.'); return; }
    const ids = Array.from(selected).slice(0, 10);
    if (ids.length === 0) return;
    if (createInCrm && !window.confirm(`Criar ${ids.length} contatos no CRM (consome ${ids.length} créditos Apollo)?`)) return;
    if (!createInCrm && !window.confirm(`Enriquecer ${ids.length} candidatos (preview; consome ${ids.length} créditos Apollo)?`)) return;

    setEnriching(true);
    setFeedback(null);
    const res = await enrichPeople({ apolloPersonIds: ids, targetEmpresaId: empresaContextId, createInCrm, decidedBy: user?.email });
    setEnriching(false);
    if (!res.success) {
      setFeedback({ type: 'error', text: res.error });
      return;
    }
    const created = res.data.created_contacts?.length || 0;
    setFeedback({
      type: 'success',
      text: createInCrm
        ? `${created} contatos criados, ${res.data.credits_used} créditos consumidos.`
        : `Preview: ${res.data.enriched?.length || 0} enriquecidos, ${res.data.credits_used} créditos consumidos. Dados: ${res.data.enriched?.slice(0, 3).map((e) => e.name).join(', ')}...`,
    });
    setSelected(new Set());
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {searchTarget === 'people' ? <Users size={18} className="text-indigo-600" /> : <Building2 size={18} className="text-indigo-600" />}
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                Resultados: {rows.length} de {totalCount} candidatos
              </h2>
              <p className="text-xs text-slate-500">
                {summary || '—'} · run_id: {runId?.slice(0, 8) || '—'}
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={18} /></button>
        </div>

        <div className="px-5 py-2 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <button onClick={() => setFilter('all')} className={`px-2.5 py-1 text-xs rounded ${filter === 'all' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            Todos ({rows.length})
          </button>
          <button onClick={() => setFilter('high')} className={`px-2.5 py-1 text-xs rounded ${filter === 'high' ? 'bg-emerald-600 text-white' : 'bg-emerald-50 text-emerald-700 hover:bg-emerald-100'}`}>
            🟢 High ({counts.high})
          </button>
          <button onClick={() => setFilter('medium')} className={`px-2.5 py-1 text-xs rounded ${filter === 'medium' ? 'bg-amber-600 text-white' : 'bg-amber-50 text-amber-700 hover:bg-amber-100'}`}>
            🟡 Medium ({counts.medium})
          </button>
          <button onClick={() => setFilter('low')} className={`px-2.5 py-1 text-xs rounded ${filter === 'low' ? 'bg-red-600 text-white' : 'bg-red-50 text-red-700 hover:bg-red-100'}`}>
            🔴 Low ({counts.low})
          </button>
          <button onClick={() => setFilter('in_crm')} className={`px-2.5 py-1 text-xs rounded ${filter === 'in_crm' ? 'bg-slate-700 text-white' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`}>
            ✅ Já no CRM ({counts.in_crm})
          </button>
        </div>

        {feedback && (
          <div className={`px-5 py-2 border-b text-xs flex items-center gap-2 ${feedback.type === 'error' ? 'bg-red-50 text-red-700 border-red-200' : 'bg-emerald-50 text-emerald-700 border-emerald-200'}`}>
            {feedback.type === 'error' ? <AlertCircle size={14} /> : <CheckCircle2 size={14} />}
            {feedback.text}
          </div>
        )}

        <div className="flex-1 overflow-auto">
          {filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Nenhum resultado com este filtro.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-slate-50 border-b border-slate-200 text-[11px] uppercase text-slate-500">
                <tr>
                  <th className="px-3 py-2 text-left w-8">
                    <input type="checkbox" checked={selected.size === filtered.length && filtered.length > 0} onChange={toggleAll} />
                  </th>
                  <th className="px-3 py-2 text-left">Priority</th>
                  <th className="px-3 py-2 text-left">Nome</th>
                  {searchTarget === 'people' ? (
                    <>
                      <th className="px-3 py-2 text-left">Cargo</th>
                      <th className="px-3 py-2 text-left">Empresa</th>
                    </>
                  ) : (
                    <>
                      <th className="px-3 py-2 text-left">Indústria</th>
                      <th className="px-3 py-2 text-right">Func</th>
                      <th className="px-3 py-2 text-left">Cidade</th>
                    </>
                  )}
                  <th className="px-3 py-2 text-center">CRM</th>
                  <th className="px-3 py-2 text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r) => {
                  const key = searchTarget === 'people' ? r.apollo_person_id : r.apollo_id;
                  const isSelected = selected.has(key);
                  return (
                    <tr key={key} className={`border-b border-slate-100 hover:bg-slate-50 ${isSelected ? 'bg-indigo-50' : ''}`}>
                      <td className="px-3 py-2">
                        <input type="checkbox" checked={isSelected} onChange={() => toggleOne(key)} disabled={r.already_in_crm && searchTarget === 'people'} />
                      </td>
                      <td className="px-3 py-2">
                        <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-medium ${PRIORITY_BADGE[r.priority] || PRIORITY_BADGE.low}`} title={r.reasoning || '—'}>
                          {r.priority}
                        </span>
                      </td>
                      <td className="px-3 py-2 text-xs text-slate-800 font-medium">{r.name || '—'}</td>
                      {searchTarget === 'people' ? (
                        <>
                          <td className="px-3 py-2 text-xs text-slate-600 truncate max-w-[200px]" title={r.title || ''}>{r.title || '—'}</td>
                          <td className="px-3 py-2 text-xs text-slate-600">{r.company || '—'}</td>
                        </>
                      ) : (
                        <>
                          <td className="px-3 py-2 text-xs text-slate-600">{r.industry || '—'}</td>
                          <td className="px-3 py-2 text-xs text-right tabular-nums">{r.num_employees || '—'}</td>
                          <td className="px-3 py-2 text-xs text-slate-600">{r.city || '—'}</td>
                        </>
                      )}
                      <td className="px-3 py-2 text-center">
                        {r.already_in_crm && <span className="text-emerald-600" title="Já existe no CRM">✅</span>}
                      </td>
                      <td className="px-3 py-2 text-right">
                        {searchTarget === 'people' && r.apollo_person_id && (
                          <a href={`https://app.apollo.io/#/people/${r.apollo_person_id}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-0.5">
                            Apollo <ExternalLink size={9} />
                          </a>
                        )}
                        {searchTarget === 'companies' && r.apollo_id && (
                          <a href={`https://app.apollo.io/#/organizations/${r.apollo_id}`} target="_blank" rel="noopener noreferrer" className="text-[10px] text-slate-500 hover:text-slate-700 inline-flex items-center gap-0.5">
                            Apollo <ExternalLink size={9} />
                          </a>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {selected.size > 0 && searchTarget === 'people' && (
          <div className="px-5 py-3 border-t border-slate-200 bg-slate-50 flex items-center gap-3">
            <span className="text-sm text-slate-700">{selected.size} selecionado(s)</span>
            <button
              onClick={() => doEnrich(false)}
              disabled={enriching || selected.size > 10}
              className="px-3 py-1.5 text-xs bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1"
              title="Preview: consome 1 crédito por pessoa, não cria no CRM"
            >
              {enriching ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Enriquecer (preview)
            </button>
            <button
              onClick={() => doEnrich(true)}
              disabled={enriching || selected.size > 10 || !empresaContextId}
              className="px-3 py-1.5 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-1"
              title={empresaContextId ? 'Cria contatos no CRM com contact_employments' : 'Selecione empresa-alvo no SearchBuilder primeiro'}
            >
              {enriching ? <Loader2 size={12} className="animate-spin" /> : <CheckCircle2 size={12} />}
              Criar {selected.size} contatos no CRM
            </button>
            <button onClick={() => setSelected(new Set())} className="px-3 py-1.5 text-xs text-slate-600 hover:bg-slate-100 rounded">Limpar seleção</button>
            <span className="ml-auto text-[11px] text-slate-500">Máximo 10 por chamada</span>
          </div>
        )}
      </div>
    </div>
  );
}
